import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { listTenants, type TenantInfo } from '../api/client';
import { TENANT_STORAGE_KEY, readStoredTenant } from '../api/config';

interface TenantContextType {
  /** Currently selected tenant (null = no selection yet, show picker) */
  tenant: TenantInfo | null;
  /** List of tenants the user has access to */
  tenants: TenantInfo[];
  /** Whether the tenant list is loading */
  loading: boolean;
  /** Select a tenant and persist the choice */
  selectTenant: (tenant: TenantInfo) => void;
  /** Clear selection and return to picker */
  clearTenant: () => void;
  /** Load tenants from the directory */
  loadTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  tenants: [],
  loading: false,
  selectTenant: () => {},
  clearTenant: () => {},
  loadTenants: async () => {},
});

export function useTenant(): TenantContextType {
  return useContext(TenantContext);
}

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * WM-4: TenantProvider manages the selected tenant globally.
 *
 * - Persists the selected tenant in localStorage, so the Tenant Picker is only
 *   shown the first time a user signs in; later visits land on the inbox.
 * - The API config (getApiBaseUrl, getMailboxId) reads the same stored record,
 *   so all API calls route to the selected tenant's Data Plane.
 */
export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantInfo | null>(
    () => readStoredTenant() as TenantInfo | null,
  );
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(false);

  const selectTenant = useCallback((t: TenantInfo) => {
    // Persist before setting state: API calls triggered by the re-render read
    // the endpoint straight out of storage.
    localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify(t));
    setTenant(t);
  }, []);

  const clearTenant = useCallback(() => {
    localStorage.removeItem(TENANT_STORAGE_KEY);
    setTenant(null);
  }, []);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listTenants();
      setTenants(list);
    } catch (err) {
      console.error('Failed to load tenants', err);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load tenants on mount
  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  return (
    <TenantContext.Provider value={{ tenant, tenants, loading, selectTenant, clearTenant, loadTenants }}>
      {children}
    </TenantContext.Provider>
  );
}
